import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PortalV2Layout from './PortalV2Layout';
import { useAuth } from '../contexts/AuthContext';
import { RefreshCw, AlertTriangle, CheckCircle, Receipt, Star, ExternalLink, MapPin } from 'lucide-react';
import StatementsPanel from './StatementsPanel';

/**
 * Admin Dashboard — V2 reskin. Mirrors V1 stat cards (active, served, in-progress,
 * unique-clients, pending approvals, approved domains, jobs this month), Top Clients,
 * Top Firms, and detailed webhook activity (each webhook log shows affected jobs).
 */
const AdminV2Dashboard = () => {
  const { authFetch } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const [topClients, setTopClients] = useState([]);
  const [topFirms, setTopFirms] = useState([]);
  const [webhookStatus, setWebhookStatus] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [calStatus, setCalStatus] = useState(null);
  const [gbpStatus, setGbpStatus] = useState(null);
  const [gbpBusy, setGbpBusy] = useState(false);
  const [gbpMsg, setGbpMsg] = useState('');
  const [coverage, setCoverage] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [reconcileBusy, setReconcileBusy] = useState(false);
  const [reconcileApplying, setReconcileApplying] = useState(false);
  const [reconcileReport, setReconcileReport] = useState(null);
  const [showReconcile, setShowReconcile] = useState(false);
  const [reconcileError, setReconcileError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const [dashRes, syncRes, hookRes, calRes, gbpRes, covRes] = await Promise.all([
        authFetch('/api/admin/dashboard'),
        authFetch('/api/admin/sync-status'),
        authFetch('/api/admin/webhook-status'),
        authFetch('/api/notary/google-calendar/status').catch(() => null),
        authFetch('/api/admin/oauth/google-business/status').catch(() => null),
        authFetch('/api/admin/coverage/searches').catch(() => null),
      ]);
      if (dashRes && dashRes.ok) {
        const d = await dashRes.json();
        setStats(d.stats || d);
        setRecentJobs(d.recent_jobs || []);
        setTopClients(d.top_clients || []);
        setTopFirms(d.top_firms || []);
      } else {
        setError('Failed to load dashboard');
      }
      if (syncRes && syncRes.ok) setSyncStatus(await syncRes.json());
      if (hookRes && hookRes.ok) setWebhookStatus(await hookRes.json());
      if (calRes && calRes.ok) setCalStatus(await calRes.json());
      if (gbpRes && gbpRes.ok) setGbpStatus(await gbpRes.json());
      if (covRes && covRes.ok) setCoverage(await covRes.json());
    } catch (e) {
      setError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const triggerSync = async () => {
    setSyncing(true);
    try {
      const res = await authFetch('/api/admin/sync-now', { method: 'POST' });
      if (res.ok) setTimeout(load, 1500);
    } finally { setSyncing(false); }
  };

  // Poll reconcile status until it finishes; returns the final status doc.
  const pollReconcile = async () => {
    for (let i = 0; i < 120; i++) {
      const r = await authFetch('/api/admin/jobs/reconcile/status');
      if (r.ok) {
        const d = await r.json();
        if (d.status === 'completed' || d.status === 'failed') return d;
      }
      await new Promise(res => setTimeout(res, 3000));
    }
    return { status: 'failed', error: 'Timed out waiting for reconcile.' };
  };

  // Dry-run: show what WOULD change.
  const openReconcile = async () => {
    setShowReconcile(true);
    setReconcileBusy(true);
    setReconcileError(null);
    setReconcileReport(null);
    try {
      const r = await authFetch('/api/admin/jobs/reconcile', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apply: false }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed to start reconcile');
      const final = await pollReconcile();
      if (final.status === 'failed') throw new Error(final.error || 'Reconcile failed');
      setReconcileReport(final.report);
    } catch (e) {
      setReconcileError(e.message);
    } finally {
      setReconcileBusy(false);
    }
  };

  // Apply: actually mirror ServeManager.
  const applyReconcile = async () => {
    setReconcileApplying(true);
    setReconcileError(null);
    try {
      const r = await authFetch('/api/admin/jobs/reconcile', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apply: true }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed to apply');
      const final = await pollReconcile();
      if (final.status === 'failed') throw new Error(final.error || 'Apply failed');
      setReconcileReport(final.report);
      setTimeout(load, 1000);
    } catch (e) {
      setReconcileError(e.message);
    } finally {
      setReconcileApplying(false);
    }
  };

  const handleGbpConnect = async () => {
    setGbpBusy(true); setGbpMsg('');
    try {
      const r = await authFetch('/api/admin/oauth/google-business/start');
      if (!r.ok) throw new Error('Failed to start OAuth');
      const { authorization_url } = await r.json();
      window.location.href = authorization_url;
    } catch (e) {
      setGbpMsg(`Error: ${e.message}`);
      setGbpBusy(false);
    }
  };

  const handleGbpSync = async () => {
    setGbpBusy(true); setGbpMsg('');
    try {
      const r = await authFetch('/api/admin/reviews/business-profile/sync', { method: 'POST' });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Sync failed');
      setGbpMsg(`Synced ${data.synced} reviews.`);
      // Refresh status
      const sRes = await authFetch('/api/admin/oauth/google-business/status').catch(() => null);
      if (sRes && sRes.ok) setGbpStatus(await sRes.json());
    } catch (e) {
      setGbpMsg(`Sync error: ${e.message}`);
    } finally {
      setGbpBusy(false);
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    const ms = Date.now() - d.getTime();
    if (ms < 60000) return 'just now';
    if (ms < 3600000) return `${Math.floor(ms/60000)}m ago`;
    if (ms < 86400000) return `${Math.floor(ms/3600000)}h ago`;
    return d.toLocaleDateString();
  };

  const calBroken = calStatus && calStatus.connected === false;
  const successRate = stats?.total_jobs
    ? Math.round((stats.served / Math.max(1, stats.total_jobs - (stats.canceled || 0))) * 100)
    : 0;

  return (
    <PortalV2Layout
      eyebrow="Control Room"
      title="Admin Dashboard"
      subtitle="Live operations overview — sync, jobs, webhooks, and team metrics."
      crumbs={<><span style={{ opacity: 0.5 }}>Admin /</span> <strong style={{ marginLeft: 6 }}>Dashboard</strong></>}
      action={
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => openReconcile()} disabled={reconcileBusy} className="pv2-btn pv2-btn-ghost" data-testid="pv2-reconcile-open">
            <RefreshCw size={14} strokeWidth={1.5} className={reconcileBusy ? 'pv2-skeleton' : ''} />
            {reconcileBusy ? 'Checking…' : 'Reconcile w/ SM'}
          </button>
          <button onClick={triggerSync} disabled={syncing} className="pv2-btn pv2-btn-ghost" data-testid="pv2-sync-now">
            <RefreshCw size={14} strokeWidth={1.5} className={syncing ? 'pv2-skeleton' : ''} />
            {syncing ? 'Syncing…' : 'Sync Now'}
          </button>
        </div>
      }
    >
      {calBroken && (
        <div className="pv2-alert pv2-alert-amber" data-testid="pv2-cal-warning">
          <AlertTriangle size={16} strokeWidth={1.5} />
          <div>
            <strong>Google Calendar reconnect required.</strong>{' '}
            <a href={`${process.env.REACT_APP_BACKEND_URL || ''}/api/notary/google-auth`} style={{ color: 'inherit', textDecoration: 'underline' }}>
              Re-authorize now
            </a>
            {calStatus?.error && <div style={{ opacity: 0.7, marginTop: 4, fontSize: 12 }}>{calStatus.error}</div>}
          </div>
        </div>
      )}
      {error && <div className="pv2-alert pv2-alert-red"><AlertTriangle size={16} /><span>{error}</span></div>}

      {/* Last sync — above stat cards */}
      {syncStatus && (
        <section style={{ marginBottom: 24 }}>
          <h2 className="pv2-display" style={{ fontSize: 18, marginBottom: 10 }}>Last Sync</h2>
          <div className="pv2-card" style={{ padding: 18 }}>
            <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
              <Meta label="Last synced" value={syncStatus.last_synced_at ? fmtTime(syncStatus.last_synced_at) : '—'} />
              <Meta label="Status" value={syncStatus.status || 'idle'} />
              <Meta label="Jobs total" value={syncStatus.total_jobs ?? '—'} />
              <Meta label="Inserted" value={syncStatus.inserted ?? '—'} />
              <Meta label="Updated" value={syncStatus.updated ?? '—'} />
            </div>
          </div>
        </section>
      )}

      {/* Primary stats */}
      <section className="pv2-stats-grid" data-testid="pv2-admin-stats">
        <Stat label="Active Jobs" value={stats?.active_jobs ?? '—'} subtext={`${stats?.total_jobs ?? 0} total · ${stats?.archived_jobs ?? 0} archived`} loading={loading} />
        <Stat label="Served"      value={stats?.served ?? '—'}      subtext={`${successRate}% success rate`} loading={loading} />
        <Stat label="In Progress" value={stats?.in_progress ?? '—'} subtext="Awaiting service" loading={loading} />
        <Stat label="Unique Clients" value={stats?.unique_clients ?? '—'} subtext={`${stats?.registered_users ?? 0} registered`} loading={loading} link="/portal/admin/clients" />
      </section>

      {/* Secondary stats: Pending Approvals / Approved Domains / Jobs This Month */}
      <section className="pv2-stats-grid pv2-stats-grid--3" style={{ marginTop: 16 }} data-testid="pv2-admin-stats-secondary">
        <Stat label="Pending Approvals"   value={stats?.pending_users ?? '—'}     loading={loading} link="/portal/admin/clients" highlight={(stats?.pending_users || 0) > 0} />
        <Stat label="Approved Domains"    value={stats?.approved_domains ?? '—'}  loading={loading} link="/portal/admin/domains" />
        <Stat label="Jobs This Month"     value={stats?.jobs_this_month ?? '—'}   loading={loading} />
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24, marginTop: 32 }}>
        {/* Recent Jobs */}
        <section>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h2 className="pv2-display" style={{ fontSize: 22 }}>Recent Jobs</h2>
            <Link to="/portal/admin/jobs" className="pv2-mono" style={{ color: 'var(--pv2-red-bright)' }}>View all →</Link>
          </div>
          <div className="pv2-table-wrap">
            <table className="pv2-table" data-testid="pv2-recent-jobs-table">
              <thead><tr><th>Job #</th><th>Recipient / Client</th><th>Status</th><th>Updated</th></tr></thead>
              <tbody>
                {loading && <tr><td colSpan={4} className="pv2-table-empty">Loading…</td></tr>}
                {!loading && recentJobs.length === 0 && <tr><td colSpan={4} className="pv2-table-empty">No recent jobs.</td></tr>}
                {!loading && recentJobs.slice(0, 8).map(j => (
                  <tr key={j.id || j.servemanager_job_id}
                    data-testid={j.is_rush ? `pv2-rush-row-${j.id || j.servemanager_job_id}` : undefined}
                    style={j.is_rush ? { background: 'rgba(248, 113, 113, 0.07)', boxShadow: 'inset 3px 0 0 0 var(--pv2-red-bright)' } : undefined}>
                    <td><Link to={`/portal/jobs/${j.id || j.servemanager_job_id}`} className="pv2-table-job-num">{j.servemanager_job_number || '—'}</Link></td>
                    <td style={{ maxWidth: 280 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={j.recipient_name || ''}>{j.recipient_name || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--pv2-text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={j.client_company_name || j.client_email || ''}>
                        {j.client_company_name || j.client_email || '—'}
                      </div>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}><StatusPill status={j.service_status || j.job_status} /></td>
                    <td className="pv2-mono" style={{ textTransform: 'none', letterSpacing: 0, fontSize: 12, whiteSpace: 'nowrap' }}>{fmtTime(j.updated_at || j.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Webhook activity (compact) */}
        <section>
          <h2 className="pv2-display" style={{ fontSize: 22, marginBottom: 16 }}>Webhook Activity</h2>
          <div className="pv2-card" style={{ maxHeight: 480, overflowY: 'auto', padding: 0 }}>
            <div className="pv2-mono-sm" style={{ padding: '14px 18px', borderBottom: '1px solid var(--pv2-ink-line-soft)' }}>
              Last webhook: {webhookStatus?.last_webhook_at ? fmtTime(webhookStatus.last_webhook_at) : '—'}
            </div>
            {(webhookStatus?.recent_logs || []).slice(0, 12).map((log, idx) => {
              // Title-case the webhook name (e.g. "invoice updated" -> "Invoice Updated")
              const niceName = (log.webhook_name || 'webhook')
                .toString()
                .replace(/[_-]/g, ' ')
                .replace(/\b\w/g, c => c.toUpperCase());
              return (
                <div key={idx} style={{ padding: '10px 18px', borderBottom: '1px solid var(--pv2-ink-line-soft)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{niceName}</span>
                    <span className="pv2-mono-sm" style={{ flexShrink: 0 }}>{fmtTime(log.received_at)}</span>
                  </div>
                  <div style={{ fontSize: 11, marginTop: 2, color: 'var(--pv2-text-muted)', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'baseline' }}>
                    {log.jobs_created > 0 && <span style={{ color: 'var(--pv2-green-text)' }}>+{log.jobs_created} new</span>}
                    {log.jobs_updated > 0 && <span style={{ color: 'var(--pv2-amber-text)' }}>{log.jobs_updated} updated</span>}
                    {!log.jobs_created && !log.jobs_updated && <span style={{ color: 'var(--pv2-text-subtle)' }}>no changes</span>}
                    {log.affected_jobs && log.affected_jobs.length > 0 && (
                      <span style={{ color: 'var(--pv2-text-subtle)' }}>·</span>
                    )}
                    {log.affected_jobs && log.affected_jobs.slice(0, 6).map((job, jIdx) => (
                      <Link
                        key={jIdx}
                        to={`/portal/jobs/${job.job_number || ''}`}
                        title={`${job.recipient || ''} · ${job.action || ''}`}
                        className="pv2-mono-sm"
                        style={{ color: job.action === 'created' ? 'var(--pv2-green-text)' : 'var(--pv2-amber-text)', textDecoration: 'none' }}
                      >
                        #{job.job_number}
                      </Link>
                    ))}
                    {log.affected_jobs && log.affected_jobs.length > 6 && (
                      <span className="pv2-mono-sm" style={{ color: 'var(--pv2-text-subtle)' }}>+{log.affected_jobs.length - 6}</span>
                    )}
                  </div>
                </div>
              );
            })}
            {(!webhookStatus?.recent_logs || webhookStatus.recent_logs.length === 0) && (
              <div style={{ fontSize: 13, color: 'var(--pv2-text-subtle)', padding: '12px 18px' }}>No recent webhooks.</div>
            )}
          </div>
        </section>
      </div>

      {/* Top Clients & Top Firms */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginTop: 32 }}>
        <section>
          <h2 className="pv2-display" style={{ fontSize: 22, marginBottom: 16 }}>Top Contacts</h2>
          <div className="pv2-card">
            {topClients.length === 0 && <div style={{ fontSize: 13, color: 'var(--pv2-text-subtle)' }}>No data yet.</div>}
            {topClients.slice(0, 5).map((c, idx) => {
              const name = c.company_name || c.client_company_name || c.name || c.client_name || c.email || c.client_email || '—';
              const email = c.email || c.client_email || '';
              return (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderTop: idx === 0 ? 'none' : '1px solid var(--pv2-ink-line-soft)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
                    <span className="pv2-mono-sm" style={{ width: 20 }}>{idx + 1}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={name}>{name}</div>
                      {email && <div className="pv2-mono-sm" style={{ marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={email}>{email}</div>}
                    </div>
                  </div>
                  <div className="pv2-display" style={{ fontSize: 18, flexShrink: 0, marginLeft: 12 }}>{c.job_count ?? c.count ?? 0}</div>
                </div>
              );
            })}
          </div>
        </section>
        <section>
          <h2 className="pv2-display" style={{ fontSize: 22, marginBottom: 16 }}>Top Firms</h2>
          <div className="pv2-card">
            {topFirms.length === 0 && <div style={{ fontSize: 13, color: 'var(--pv2-text-subtle)' }}>No data yet.</div>}
            {topFirms.slice(0, 5).map((f, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderTop: idx === 0 ? 'none' : '1px solid var(--pv2-ink-line-soft)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <span className="pv2-mono-sm" style={{ width: 20 }}>{idx + 1}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{f.company_name || f.firm_name || f.name || '—'}</div>
                    {(f.contact_count > 0) && (
                      <div className="pv2-mono-sm" style={{ marginTop: 2 }}>
                        {f.contact_count} {f.contact_count === 1 ? 'contact' : 'contacts'}
                      </div>
                    )}
                  </div>
                </div>
                <div className="pv2-display" style={{ fontSize: 18 }}>{f.job_count ?? f.count ?? 0}</div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Integrations: Google Business Profile */}
      <section style={{ marginTop: 32 }}>
        <h2 className="pv2-display" style={{ fontSize: 22, marginBottom: 16 }}>Integrations</h2>
        <div className="pv2-card" style={{ padding: 24 }} data-testid="pv2-gbp-panel">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ minWidth: 0, flex: '1 1 320px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <Star size={16} strokeWidth={1.5} style={{ color: gbpStatus?.connected ? 'var(--pv2-green-text)' : 'var(--pv2-text-subtle)' }} />
                <h3 className="pv2-display" style={{ fontSize: 16, margin: 0 }}>Google Business Profile</h3>
              </div>
              <p style={{ fontSize: 13, color: 'var(--pv2-text-muted)', margin: 0, lineHeight: 1.5 }}>
                {gbpStatus?.connected ? (
                  <>
                    Connected. Last sync:{' '}
                    <span style={{ color: '#fff', fontWeight: 500 }}>
                      {gbpStatus.last_sync_at ? new Date(gbpStatus.last_sync_at).toLocaleString() : '— never'}
                    </span>
                    {typeof gbpStatus.last_sync_count === 'number' && <> · {gbpStatus.last_sync_count} reviews cached</>}
                  </>
                ) : (
                  'Not connected. Authorize once to enable nightly review sync and unlock the full reviews carousel on the marketing site.'
                )}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
              <button
                onClick={handleGbpConnect}
                disabled={gbpBusy}
                className="pv2-btn pv2-btn-ghost"
                data-testid="pv2-gbp-connect"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                <ExternalLink size={14} strokeWidth={1.5} />
                {gbpStatus?.connected ? 'Re-authorize' : 'Connect'}
              </button>
              {gbpStatus?.connected && (
                <button
                  onClick={handleGbpSync}
                  disabled={gbpBusy}
                  className="pv2-btn pv2-btn-primary"
                  data-testid="pv2-gbp-sync"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                >
                  <RefreshCw size={14} strokeWidth={1.5} className={gbpBusy ? 'pv2-skeleton' : ''} />
                  Sync Reviews
                </button>
              )}
            </div>
          </div>
          {gbpMsg && (
            <p style={{ marginTop: 12, fontSize: 13, color: gbpMsg.startsWith('Error') || gbpMsg.startsWith('Sync error') ? 'var(--pv2-red-bright)' : 'var(--pv2-green-text)' }} data-testid="pv2-gbp-msg">
              {gbpMsg}
            </p>
          )}
        </div>
      </section>

      {/* Client Statements — monthly + manual send */}
      <StatementsPanel />

      {/* Coverage Searches — demand heat-map from the pricing calculator */}
      <section style={{ marginTop: 32 }} data-testid="pv2-coverage-panel">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <MapPin size={18} strokeWidth={1.5} style={{ color: 'var(--pv2-red-bright)' }} />
            <h2 className="pv2-display" style={{ fontSize: 22, margin: 0 }}>Coverage Searches</h2>
          </div>
          <span className="pv2-mono-sm" data-testid="pv2-coverage-total">{coverage ? `${coverage.total} total` : '—'}</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <div className="pv2-card">
            <div className="pv2-mono-sm" style={{ marginBottom: 10 }}>Most-searched addresses</div>
            {(!coverage || coverage.top_towns.length === 0) && <div style={{ fontSize: 13, color: 'var(--pv2-text-subtle)' }}>No searches yet.</div>}
            {coverage && coverage.top_towns.slice(0, 8).map((t, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: i === 0 ? 'none' : '1px solid var(--pv2-ink-line-soft)' }}>
                <span style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.name}>{t.name}</span>
                <span className="pv2-display" style={{ fontSize: 16, flexShrink: 0, marginLeft: 12 }}>{t.count}</span>
              </div>
            ))}
          </div>
          <div className="pv2-card">
            <div className="pv2-mono-sm" style={{ marginBottom: 10 }}>Out-of-hub (network) searches</div>
            {(!coverage || coverage.out_of_hub.length === 0) && <div style={{ fontSize: 13, color: 'var(--pv2-text-subtle)' }}>None yet.</div>}
            {coverage && coverage.out_of_hub.slice(0, 8).map((t, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: i === 0 ? 'none' : '1px solid var(--pv2-ink-line-soft)' }}>
                <span style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.name}>{t.name}</span>
                <span className="pv2-display" style={{ fontSize: 16, flexShrink: 0, marginLeft: 12, color: 'var(--pv2-amber-text)' }}>{t.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="pv2-card" style={{ marginTop: 16, padding: 0 }}>
          <div className="pv2-mono-sm" style={{ padding: '14px 18px', borderBottom: '1px solid var(--pv2-ink-line-soft)' }}>Recent searches</div>
          <div style={{ maxHeight: 360, overflowY: 'auto' }} data-testid="pv2-coverage-recent">
            {(!coverage || coverage.recent.length === 0) && <div style={{ padding: 18, fontSize: 13, color: 'var(--pv2-text-subtle)' }}>No searches logged yet. They'll appear here as visitors use the pricing calculator.</div>}
            {coverage && coverage.recent.map((r, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '10px 18px', borderTop: i === 0 ? 'none' : '1px solid var(--pv2-ink-line-soft)' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.formatted || r.query}>{r.formatted || r.query || '—'}</div>
                  <div className="pv2-mono-sm" style={{ marginTop: 2 }}>
                    {r.kind === 'hub' ? `${r.hub} · Zone ${r.zone} · ${r.routine}` : (r.kind === 'nm' ? 'New Mexico · flat rate' : 'Nationwide network')}
                    {r.geo_city ? ` — from ${r.geo_city}, ${r.geo_region}` : ''}
                  </div>
                </div>
                <span className="pv2-mono-sm" style={{ flexShrink: 0 }}>{fmtTime(r.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {showReconcile && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', zIndex: 100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: 32 }} data-testid="pv2-reconcile-modal">
          <div style={{ width: '100%', maxWidth: 640, background: 'var(--pv2-ink)', border: '1px solid var(--pv2-ink-line)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid var(--pv2-ink-line)' }}>
              <div>
                <div className="pv2-mono-sm">ServeManager</div>
                <div style={{ fontSize: 16, fontWeight: 500, marginTop: 2 }}>Reconcile Jobs</div>
              </div>
              <button onClick={() => setShowReconcile(false)} style={{ background: 'transparent', border: '1px solid var(--pv2-ink-line)', color: '#fff', padding: 8, cursor: 'pointer' }} data-testid="pv2-reconcile-close">✕</button>
            </div>
            <div style={{ padding: 24 }}>
              {reconcileBusy && <div style={{ textAlign: 'center', padding: 24, color: 'var(--pv2-text-muted)' }}>Comparing the portal against ServeManager… this can take a minute or two.</div>}
              {reconcileError && <div className="pv2-alert pv2-alert-red" style={{ marginBottom: 16 }} data-testid="pv2-reconcile-error"><span>{reconcileError}</span></div>}
              {reconcileReport && (
                <div data-testid="pv2-reconcile-report">
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 20 }}>
                    <Stat2 label="ServeManager Active" value={reconcileReport.sm_active} />
                    <Stat2 label="Portal Active (before)" value={reconcileReport.portal_active_before} />
                    <Stat2 label="Will mark Archived" value={reconcileReport.will_mark_archived} color="var(--pv2-amber-text)" />
                    <Stat2 label="Will delete (gone from SM)" value={reconcileReport.will_delete} color="var(--pv2-red-bright)" />
                    <Stat2 label="Will add (missing here)" value={reconcileReport.will_add} color="#60a5fa" />
                    {reconcileReport.portal_active_after != null && <Stat2 label="Portal Active (after)" value={reconcileReport.portal_active_after} color="var(--pv2-green-text)" />}
                  </div>
                  {!!(reconcileReport.marked_archived_ids || []).length && (
                    <CulpritList title={`Archived in SM but Active here (${reconcileReport.will_mark_archived})`} ids={reconcileReport.marked_archived_ids} />
                  )}
                  {!!(reconcileReport.deleted_ids || []).length && (
                    <CulpritList title={`Deleted from ServeManager (${reconcileReport.will_delete})`} ids={reconcileReport.deleted_ids} />
                  )}
                  {reconcileReport.portal_active_after == null ? (
                    <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                      <button onClick={applyReconcile} disabled={reconcileApplying} className="pv2-btn pv2-btn-primary" style={{ flex: 1 }} data-testid="pv2-reconcile-apply">
                        {reconcileApplying ? 'Applying…' : 'Apply Fix — mirror ServeManager'}
                      </button>
                      <button onClick={() => setShowReconcile(false)} className="pv2-btn pv2-btn-ghost" data-testid="pv2-reconcile-cancel">Cancel</button>
                    </div>
                  ) : (
                    <div className="pv2-alert pv2-alert-green" style={{ marginTop: 20 }} data-testid="pv2-reconcile-done">
                      <CheckCircle size={14} /><span>Done — the portal now mirrors ServeManager.</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}


    </PortalV2Layout>
  );
};

const Stat = ({ label, value, subtext, loading, link, highlight }) => {
  const Inner = (
    <>
      <div className="pv2-stat-label">{label}</div>
      <div className="pv2-stat-value" style={{ color: highlight ? 'var(--pv2-amber-text)' : undefined }} data-testid={`pv2-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
        {loading ? <span className="pv2-skeleton" style={{ display: 'inline-block', width: 80, height: 36 }}>&nbsp;</span> : value}
      </div>
      {subtext && <div className="pv2-stat-meta">{subtext}</div>}
    </>
  );
  return link
    ? <Link to={link} className="pv2-stat" style={{ textDecoration: 'none', color: 'inherit' }}>{Inner}</Link>
    : <div className="pv2-stat">{Inner}</div>;
};

const Meta = ({ label, value }) => (
  <div>
    <div className="pv2-mono-sm" style={{ marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 14, fontWeight: 500 }}>{value}</div>
  </div>
);

// Color mapping for status text — V1 parity (text-only, no border/box)
// "Posted" in ServeManager = successfully served → green
export const statusTextColor = (status) => {
  if (!status) return 'var(--pv2-text-muted)';
  const s = String(status).toLowerCase();
  if (/(served|completed|posted)/.test(s)) return 'var(--pv2-green-text)';
  if (/(filed|affidavit)/.test(s)) return 'var(--pv2-green-text)';
  if (/(bad|non.?service|fail)/.test(s)) return 'var(--pv2-red-bright)';
  if (/(cancel)/.test(s)) return 'var(--pv2-text-subtle)';
  if (/(received)/.test(s)) return '#60a5fa';
  if (/(attempt)/.test(s)) return 'var(--pv2-amber-text)';
  if (/(pending|in.?progress|out for service)/.test(s)) return '#60a5fa';
  return 'var(--pv2-text-muted)';
};

export const StatusPill = ({ status }) => {
  if (!status) return <span className="pv2-pill" style={{ color: 'var(--pv2-text-muted)' }}>Unknown</span>;
  return <span className="pv2-pill" style={{ color: statusTextColor(status) }}>{status}</span>;
};

const Stat2 = ({ label, value, color }) => (
  <div style={{ padding: 14, background: 'var(--pv2-ink-soft)', border: '1px solid var(--pv2-ink-line-soft)' }}>
    <div className="pv2-mono-sm" style={{ marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 22, fontFamily: 'Playfair Display, serif', fontWeight: 700, color: color || '#fff' }}>{value ?? '—'}</div>
  </div>
);

const CulpritList = ({ title, ids }) => (
  <div style={{ marginBottom: 16 }}>
    <div className="pv2-mono-sm" style={{ marginBottom: 6 }}>{title}</div>
    <div style={{ maxHeight: 140, overflowY: 'auto', fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: 'var(--pv2-text-muted)', background: 'var(--pv2-ink-soft)', border: '1px solid var(--pv2-ink-line-soft)', padding: '8px 12px', lineHeight: 1.7 }}>
      {ids.map((i) => <span key={i} style={{ marginRight: 12 }}>{i}</span>)}
    </div>
  </div>
);

export default AdminV2Dashboard;
